import React, { useCallback, useEffect } from 'react';

import { Box } from '@chakra-ui/react';
import { useSelector, batch, useDispatch } from 'react-redux';
import { setAllTimelines, setTimelinesData, updateSelectedTimelines } from 'reducers/okrsPageSlice';
import { useSelectedView } from 'reducers/viewsSlice';
import { useGoalAnalytics } from 'screens/analytics/goals/GoalAnalyticsContext';
import { okrsInsightsSelector } from 'selectors/okrsInsightsSelector';
import { checkNewNavEnabled } from 'utils/helpers/featureFlags';

import { isTimeBoundPeriodValid } from 'constants/helper';
import { localStorageKeys } from 'constants/localStorageKeys';

import { getTimeBoundTimePeriods } from 'services/admin';
import { showErrorSnackbar } from 'services/snackbar';

import { BreadCrumbHeader } from './BreadCrumbHeader';
import Heading from './Heading';
import OKRHeaderTabsV2 from './OKRHeaderTabsV2';

export const setTimeBoundsToLS = (timeBounds) => {
  try {
    window.localStorage.setItem(localStorageKeys.teamPageGoalsTimeBounds, JSON.stringify(timeBounds));
  } catch (e) {
    console.error('Error in saving to LS: ', e);
  }
};

const getTimeBoundsFromLS = () => {
  try {
    const timeBounds = JSON.parse(window.localStorage.getItem(localStorageKeys.teamPageGoalsTimeBounds));
    return timeBounds || [];
  } catch (e) {
    console.error('Error in reading LS: ', e);
  }
};

const Header = ({ teamName = '', showTimeline = true }) => {
  const dispatch = useDispatch();
  const { isInsightsClickThroughViewActive } = useSelector(okrsInsightsSelector);

  const view = useSelectedView();

  const { updateTimeBoundPeriods } = useGoalAnalytics();

  const getTimePeriodsData = useCallback(() => {
    getTimeBoundTimePeriods()
      .then((timePeriodData) => {
        const { previous, current, next, other } = timePeriodData;
        const allTimePeriods = [previous, current, next, ...other].filter((item) => item !== null);

        batch(() => {
          dispatch(setTimelinesData(timePeriodData));
          dispatch(setAllTimelines(allTimePeriods));
        });

        const timeBoundPeriodsFromLS = getTimeBoundsFromLS();
        // read from LS and check if it's still valid from latest fetched timeBound list
        if (
          Array.isArray(timeBoundPeriodsFromLS) &&
          timeBoundPeriodsFromLS.length > 0 &&
          isTimeBoundPeriodValid(allTimePeriods, timeBoundPeriodsFromLS[0])
        ) {
          dispatch(updateSelectedTimelines(timeBoundPeriodsFromLS));
          updateTimeBoundPeriods(timeBoundPeriodsFromLS);
        } else {
          dispatch(updateSelectedTimelines([current]));
          updateTimeBoundPeriods(current);
          setTimeBoundsToLS([current]);
        }
      })
      .catch((err) => dispatch(showErrorSnackbar(err.message)));
  }, [updateTimeBoundPeriods, dispatch]);

  useEffect(() => {
    getTimePeriodsData();
  }, [getTimePeriodsData]);

  return (
    <Box>
      {isInsightsClickThroughViewActive ? (
        <BreadCrumbHeader />
      ) : (
        <Heading showTimeline={showTimeline} teamName={teamName || view?.name || ''} />
      )}

      {!checkNewNavEnabled() && <OKRHeaderTabsV2 hideTabs={isInsightsClickThroughViewActive} />}
    </Box>
  );
};

export default Header;
